import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { describePasswordViolations, PASSWORD_POLICY_HINT } from '@weloze/shared';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Field } from '../components/ui';
import { AuthLayout } from './AuthLayout';

/**
 * Definição de senha no primeiro acesso e redefinição pelo link do e-mail —
 * a mesma tela, porque o fluxo é idêntico do ponto de vista do usuário.
 */
export function ResetPasswordPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const violations = useMemo(() => describePasswordViolations(password), [password]);
  const mismatch = confirmation.length > 0 && confirmation !== password;
  const canSubmit = password.length > 0 && violations.length === 0 && !mismatch;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFields({});
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      navigate('/login', {
        replace: true,
        state: { notice: 'Senha definida. Entre com a nova senha.' },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields ?? {});
      } else {
        setError('Erro inesperado. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout title="Defina sua senha" subtitle="Escolha uma senha para acessar o portal.">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && <Alert>{error}</Alert>}
        {fields.token && <Alert>{fields.token}</Alert>}

        <Field
          label="Nova senha"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          autoFocus
          value={password}
          error={fields.password}
          hint={PASSWORD_POLICY_HINT}
          onChange={(e) => setPassword(e.target.value)}
        />

        {/* A política é a mesma constante que o servidor aplica: o usuário vê o
            que falta enquanto digita, em vez de descobrir levando erro. */}
        {password.length > 0 && violations.length > 0 && (
          <ul className="space-y-1">
            {violations.map((problem) => (
              <li key={problem} className="flex items-center gap-1.5 text-xs text-slate-600">
                <X className="h-3.5 w-3.5 text-red-500" aria-hidden />
                {problem}
              </li>
            ))}
          </ul>
        )}
        {password.length > 0 && violations.length === 0 && (
          <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
            <Check className="h-3.5 w-3.5" aria-hidden />
            Senha dentro da política.
          </p>
        )}

        <Field
          label="Confirme a senha"
          name="confirmation"
          type="password"
          autoComplete="new-password"
          required
          value={confirmation}
          error={mismatch ? 'As senhas não coincidem.' : undefined}
          onChange={(e) => setConfirmation(e.target.value)}
        />

        <Button type="submit" className="w-full" size="lg" loading={submitting} disabled={!canSubmit}>
          Salvar senha
        </Button>

        <div className="pt-1 text-center">
          <Link to="/login" className="text-sm text-brand-600 underline-offset-4 hover:underline">
            Voltar ao login
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
